import React, { useState, useEffect } from 'react';
import { BotState } from '../types';
import { botStateAPI } from '../services/api';
import { useError } from '../contexts/ErrorContext';
import LoadingSpinner from './LoadingSpinner';
import './BotControl.css';

const BotControl: React.FC = () => {
  const { showErrorNotification } = useError();
  const [botState, setBotState] = useState<BotState>({
    enabled: true,
    autoSendEnabled: true,
    scheduleSendingEnabled: true,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    loadBotState();
  }, []);

  const loadBotState = async () => {
    try {
      const state = await botStateAPI.get();
      setBotState(state);
    } catch (error) {
      console.error('Failed to load bot state:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateBotState = async (field: keyof BotState, value: boolean) => {
    const previousState = { ...botState };
    const fieldNames = {
      enabled: 'Бот',
      autoSendEnabled: 'Автоматическая отправка',
      scheduleSendingEnabled: 'Отправка расписания'
    };
    
    try {
      setIsUpdating(true);
      // Оптимистично обновляем UI
      const newState = { ...botState, [field]: value };
      setBotState(newState);
      
      const result = await botStateAPI.update(newState);
      
      if (result.success) {
        setBotState(result.state);
        console.log(`${fieldNames[field]} ${value ? 'включен' : 'выключен'} (Kafka)`);
      } else {
        // Если success: false, возвращаем предыдущее состояние
        setBotState(previousState);
        const errorMsg = (result as any).message || 'Ошибка обновления состояния бота';
        showErrorNotification(
          `Не удалось обновить ${fieldNames[field].toLowerCase()}`,
          'error',
          errorMsg.includes('kafka') ? 'Проблема с подключением к Kafka. Проверьте, что Kafka сервис запущен и доступен.' : errorMsg
        );
      }
    } catch (error: any) {
      console.error('Failed to update bot state:', error);
      // Возвращаем предыдущее состояние при ошибке
      setBotState(previousState);
      
      let errorMessage = 'Не удалось обновить состояние бота';
      let errorDetails = 'Проверьте подключение к серверу';
      
      if (error.response?.data?.error === 'kafka_publish_failed') {
        errorMessage = `Не удалось обновить ${fieldNames[field].toLowerCase()}`;
        errorDetails = error.response.data.message || 'Проблема с подключением к Kafka. Проверьте, что Kafka сервис запущен и доступен.';
      } else if (error.message) {
        errorDetails = error.message;
      }
      
      showErrorNotification(errorMessage, 'error', errorDetails, () => {
        updateBotState(field, value);
      });
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bot-control">
        <LoadingSpinner text="Загрузка состояния бота..." />
      </div>
    );
  }

  return (
    <div className="bot-control">
      <h3>Управление ботом</h3>
      <div className="bot-switches">
        {isUpdating && (
          <div className="updating-indicator">
            <LoadingSpinner size="small" text="Обновление..." />
          </div>
        )}
        <label className="switch-item">
          <span>Бот через Kafka</span>
          <input
            type="checkbox"
            checked={botState.enabled}
            onChange={(e) => updateBotState('enabled', e.target.checked)}
            disabled={isUpdating}
          />
        </label>
        <label className="switch-item">
          <span>Автоматическая отправка</span>
          <input
            type="checkbox"
            checked={botState.autoSendEnabled}
            onChange={(e) => updateBotState('autoSendEnabled', e.target.checked)}
            disabled={isUpdating}
          />
        </label>
        <label className="switch-item">
          <span>Отправка расписания</span>
          <input
            type="checkbox"
            checked={botState.scheduleSendingEnabled}
            onChange={(e) => updateBotState('scheduleSendingEnabled', e.target.checked)}
            disabled={isUpdating}
          />
        </label>
      </div>
    </div>
  );
};

export default BotControl;
